// ============================================================
// 📁 فایل صفحه ورود فعلی (همان که export default function Auth دارد) را با این جایگزین کنید
// ============================================================
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { authExt, passwordScore } from "../../services/usersApi";
import QrCode from "../../components/QrCode";
import { Eye, EyeOff, LogIn, User, Lock, ShieldCheck, Smartphone, KeyRound, ArrowRight } from "lucide-react";

type Step = "login" | "otp" | "setup2fa" | "backup" | "changepw";

export default function Auth() {
  const { company, setCurrentUser, isLoggedIn, loading } = useApp();
  const navigate = useNavigate();
  const [dark, setDark] = useState(true);
  const [step, setStep] = useState<Step>("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [otp, setOtp] = useState("");
  const [ticket, setTicket] = useState("");
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [backup, setBackup] = useState<string[]>([]);
  const [pending, setPending] = useState<any>(null);
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [minLen, setMinLen] = useState(10);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!loading && isLoggedIn && step !== "backup") navigate("/dashboard");
  }, [isLoggedIn, loading, navigate, step]);

  const finish = (res: any) => {
    localStorage.setItem("auth_token", res.token);
    setCurrentUser({
      id: res.user.id,
      username: res.user.username,
      fullName: res.user.fullName || res.user.full_name,
      email: res.user.email,
      phone: res.user.phone,
      role: res.user.role,
      avatar: res.user.avatar,
      permissions: res.user.permissions || {},
    });
    navigate("/dashboard");
  };

  const handleLogin = async (withOtp?: string) => {
    if (!username || !password) { setError("لطفاً نام کاربری و پسورد را وارد کنید"); return; }
    setIsLoading(true); setError(""); setInfo("");
    try {
      const res = await authExt.login(username, password, remember, withOtp);
      if (res.success) finish(res);
      else if (res.need_otp) { setStep("otp"); if (withOtp) setError(res.message || "کد اشتباه است"); setOtp(""); }
      else if (res.need_2fa_setup) {
        setTicket(res.ticket);
        const s = await authExt.twofaSetupStart(res.ticket);
        if (s.success) { setSetup({ secret: s.secret, uri: s.uri }); setStep("setup2fa"); setOtp(""); } else setError(s.message || "خطا");
      } else if (res.need_password_change) { setTicket(res.ticket); setMinLen(res.pw_min_length || 10); setNewPw(""); setNewPw2(""); setStep("changepw"); }
      else setError(res.message || "نام کاربری یا پسورد اشتباه است");
    } catch { setError("خطا در اتصال به سرور. لطفاً دوباره تلاش کنید"); }
    setIsLoading(false);
  };

  const finishSetup = async () => {
    setIsLoading(true); setError("");
    try {
      const res = await authExt.twofaSetupFinish(ticket, otp);
      if (res.success) { setPending(res); setBackup(res.backupCodes || []); setStep("backup"); }
      else setError(res.message || "کد اشتباه است");
    } catch { setError("خطا در اتصال به سرور"); }
    setIsLoading(false);
  };

  const submitNewPw = async () => {
    if (newPw !== newPw2) { setError("پسورد جدید و تکرار آن یکی نیست"); return; }
    setIsLoading(true); setError("");
    try {
      const res = await authExt.changePasswordTicket(ticket, newPw);
      if (res.success) { setPassword(""); setStep("login"); setInfo("پسورد جدید ثبت شد. اکنون با آن وارد شوید."); }
      else setError(res.message || "خطا");
    } catch { setError("خطا در اتصال به سرور"); }
    setIsLoading(false);
  };

  const back = () => { setStep("login"); setError(""); setInfo(""); setOtp(""); setSetup(null); };

  const c = {
    text: dark ? "#f1f5f9" : "#0f172a",
    sub: dark ? "#64748b" : "#94a3b8",
    lab: dark ? "#94a3b8" : "#64748b",
    inp: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    bd: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
  };
  const field = { display: "flex", alignItems: "center", gap: 10, background: c.inp, border: `1.5px solid ${c.bd}`, borderRadius: 12, padding: "11px 14px" } as const;
  const bare = { flex: 1, background: "none", border: "none", outline: "none", color: c.text, fontSize: 13, fontFamily: "inherit" } as const;
  const primary = (disabled: boolean) => ({
    width: "100%", padding: "13px", background: disabled ? "rgba(59,130,246,0.5)" : "linear-gradient(135deg,#3b82f6,#8b5cf6)", border: "none", borderRadius: 12,
    cursor: disabled ? "not-allowed" : "pointer", color: "white", fontSize: 14, fontWeight: 700, fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: disabled ? "none" : "0 4px 20px rgba(59,130,246,0.4)",
  } as const);
  const spinner = <div style={{ width: 18, height: 18, border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid white", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />;

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(160deg,#070714 0%,#0e0e2a 100%)" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, border: "3px solid rgba(59,130,246,0.2)", borderTop: "3px solid #3b82f6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <div style={{ color: "#64748b", fontSize: 14 }}>در حال بررسی...</div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const title = step === "login" ? "ورود به سیستم" : step === "otp" ? "تایید دو مرحله‌ای" : step === "setup2fa" ? "فعال‌سازی 2FA" : step === "backup" ? "کدهای پشتیبان" : "تعیین پسورد جدید";
  const subtitle = step === "login" ? "نام کاربری و پسورد خود را وارد کنید" : step === "otp" ? "کد ۶ رقمی برنامه‌ی احراز هویت (یا یک کد پشتیبان) را وارد کنید" : step === "setup2fa" ? "طبق سیاست امنیتی شرکت، ابتدا باید 2FA را فعال کنید" : step === "backup" ? "این کدها را در جای امن نگه دارید؛ هر کدام یک‌بار قابل استفاده است" : "برای ادامه باید پسورد جدید و قوی تعیین کنید";
  const sc = passwordScore(newPw, minLen);

  return (
    <div dir="rtl" style={{ minHeight: "100vh", display: "flex", fontFamily: "'Vazirmatn','Segoe UI',sans-serif", position: "relative", overflow: "hidden", background: dark ? "linear-gradient(160deg,#070714 0%,#0e0e2a 100%)" : "linear-gradient(160deg,#f8faff 0%,#eef2ff 100%)" }}>
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <div style={{ position: "absolute", width: 700, height: 700, borderRadius: "50%", filter: "blur(130px)", background: dark ? "rgba(59,130,246,0.1)" : "rgba(59,130,246,0.06)", top: "-200px", right: "-100px" }} />
        <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", filter: "blur(120px)", background: dark ? "rgba(139,92,246,0.09)" : "rgba(139,92,246,0.05)", bottom: "-150px", left: "-100px" }} />
        <div style={{ position: "absolute", width: 380, height: 380, borderRadius: "50%", filter: "blur(110px)", background: "rgba(212,175,55,0.08)", top: "30%", left: "35%" }} />
      </div>

      <button onClick={() => setDark(!dark)} style={{ position: "absolute", top: 20, left: 20, zIndex: 10, width: 40, height: 40, borderRadius: 10, background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", border: `1px solid ${c.bd}`, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {dark ? "☀️" : "🌙"}
      </button>

      <div className="login-left" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 40px", position: "relative", borderLeft: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}` }}>
        <div style={{ maxWidth: 440, width: "100%" }}>
          <div style={{ width: 90, height: 90, borderRadius: 24, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 28, boxShadow: "0 12px 40px rgba(59,130,246,0.4)", overflow: "hidden", outline: "2px solid rgba(212,175,55,0.45)", outlineOffset: 3 }}>
            {company.logo ? <img src={company.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 40, fontWeight: 900, color: "white" }}>{company.name.charAt(0)}</span>}
          </div>
          <h1 style={{ fontSize: 34, fontWeight: 900, color: c.text, margin: "0 0 10px" }}>{company.name}</h1>
          <p style={{ fontSize: 15, color: c.sub, margin: "0 0 44px", lineHeight: 1.8 }}>سیستم جامع مدیریت کسب‌وکار</p>
          {[
            { icon: "🔐", title: "امنیت پیشرفته", desc: "احراز هویت دو مرحله‌ای و ثبت فعالیت‌ها" },
            { icon: "📊", title: "گزارش‌های دقیق", desc: "تحلیل کامل عملکرد کسب‌وکار" },
            { icon: "👥", title: "چند کاربره", desc: "مدیریت تیم با نقش‌ها و تاییدها" },
            { icon: "💰", title: "چند ارزی", desc: "پشتیبانی از ارزهای مختلف" },
          ].map((f, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "13px 16px", borderRadius: 12, marginBottom: 8, background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.8)", border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
              <span style={{ fontSize: 22 }}>{f.icon}</span>
              <div><div style={{ fontSize: 13, fontWeight: 700, color: dark ? "#e2e8f0" : "#0f172a" }}>{f.title}</div><div style={{ fontSize: 11, color: c.sub, marginTop: 2 }}>{f.desc}</div></div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 32px" }}>
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div style={{ position: "relative", borderRadius: 24, padding: "44px 40px", background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.98)", border: `1px solid ${c.bd}`, backdropFilter: "blur(24px)", boxShadow: dark ? "0 32px 100px rgba(0,0,0,0.6)" : "0 32px 100px rgba(0,0,0,0.12)" }}>
            <div style={{ position: "absolute", top: 0, left: "14%", right: "14%", height: 2, background: "linear-gradient(90deg,transparent,#d4af37,transparent)" }} />
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: "linear-gradient(135deg,#3b82f6,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", overflow: "hidden", boxShadow: "0 4px 16px rgba(59,130,246,0.4)", color: "#fff" }}>
                {step === "login" ? (company.logo ? <img src={company.logo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 22, fontWeight: 900 }}>{company.name.charAt(0)}</span>)
                  : step === "changepw" ? <KeyRound size={24} /> : step === "backup" ? <ShieldCheck size={24} /> : <Smartphone size={24} />}
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: c.text, margin: "0 0 6px" }}>{title}</h2>
              <p style={{ fontSize: 13, color: c.sub, margin: 0, lineHeight: 1.8 }}>{subtitle}</p>
            </div>

            {error && <div style={{ padding: "11px 14px", borderRadius: 10, marginBottom: 18, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", fontSize: 13, textAlign: "center", fontWeight: 500 }}>⚠️ {error}</div>}
            {info && <div style={{ padding: "11px 14px", borderRadius: 10, marginBottom: 18, background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", color: "#10b981", fontSize: 13, textAlign: "center", fontWeight: 500 }}>✅ {info}</div>}

            {step === "login" && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.lab, display: "block", marginBottom: 7 }}>نام کاربری</label>
                  <div style={field}><User size={15} color={c.sub} />
                    <input value={username} onChange={(e) => setUsername(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} placeholder="نام کاربری را وارد کنید" autoComplete="username" style={{ ...bare, direction: "rtl" }} /></div>
                </div>
                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.lab, display: "block", marginBottom: 7 }}>پسورد</label>
                  <div style={field}><Lock size={15} color={c.sub} />
                    <input value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleLogin()} type={showPass ? "text" : "password"} placeholder="پسورد را وارد کنید" autoComplete="current-password" style={{ ...bare, direction: "rtl" }} />
                    <button onClick={() => setShowPass(!showPass)} style={{ background: "none", border: "none", cursor: "pointer", color: c.sub, display: "flex", padding: 0 }}>{showPass ? <EyeOff size={15} /> : <Eye size={15} />}</button></div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => setRemember(!remember)}>
                    <div style={{ width: 17, height: 17, borderRadius: 5, flexShrink: 0, background: remember ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : "transparent", border: `2px solid ${remember ? "#3b82f6" : dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {remember && <span style={{ color: "white", fontSize: 10, fontWeight: 700 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 12, color: c.lab, userSelect: "none" }}>مرا به خاطر بسپار</span>
                  </div>
                  <a href={authExt.forgotUrl} style={{ fontSize: 12, color: "#3b82f6", fontWeight: 600, textDecoration: "none" }}>فراموشی پسورد؟</a>
                </div>
                <button onClick={() => handleLogin()} disabled={isLoading} style={primary(isLoading)}>{isLoading ? spinner : <><LogIn size={17} /> ورود به سیستم</>}</button>
              </>
            )}

            {step === "otp" && (
              <>
                <div style={{ ...field, marginBottom: 20 }}><ShieldCheck size={15} color={c.sub} />
                  <input autoFocus value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9A-Za-z-]/g, ""))} onKeyDown={(e) => e.key === "Enter" && otp && handleLogin(otp)} placeholder="123456" inputMode="numeric" autoComplete="one-time-code" style={{ ...bare, direction: "ltr", textAlign: "center", letterSpacing: 6, fontSize: 18 }} /></div>
                <button onClick={() => handleLogin(otp)} disabled={isLoading || otp.length < 6} style={primary(isLoading || otp.length < 6)}>{isLoading ? spinner : "تایید و ورود"}</button>
                <button onClick={back} style={{ width: "100%", marginTop: 12, background: "none", border: "none", color: c.lab, fontSize: 12, cursor: "pointer", fontFamily: "inherit", display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}><ArrowRight size={13} /> بازگشت</button>
              </>
            )}

            {step === "setup2fa" && setup && (
              <>
                <p style={{ fontSize: 12, color: c.lab, lineHeight: 1.9, margin: "0 0 12px" }}>در گوشی، برنامه‌ی Google Authenticator یا Microsoft Authenticator را باز و این QR را اسکن کنید:</p>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><QrCode value={setup.uri} size={170} /></div>
                <div style={{ fontSize: 11, color: c.sub, textAlign: "center", marginBottom: 4 }}>یا کد دستی:</div>
                <div style={{ direction: "ltr", textAlign: "center", fontFamily: "monospace", fontSize: 12, color: c.text, wordBreak: "break-all", marginBottom: 16 }}>{setup.secret}</div>
                <div style={{ ...field, marginBottom: 18 }}><ShieldCheck size={15} color={c.sub} />
                  <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))} onKeyDown={(e) => e.key === "Enter" && otp.length >= 6 && finishSetup()} placeholder="کد ۶ رقمی" inputMode="numeric" maxLength={8} style={{ ...bare, direction: "ltr", textAlign: "center", letterSpacing: 6, fontSize: 18 }} /></div>
                <button onClick={finishSetup} disabled={isLoading || otp.length < 6} style={primary(isLoading || otp.length < 6)}>{isLoading ? spinner : "تایید و فعال‌سازی"}</button>
                <button onClick={back} style={{ width: "100%", marginTop: 12, background: "none", border: "none", color: c.lab, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>انصراف</button>
              </>
            )}

            {step === "backup" && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                  {backup.map((b) => <div key={b} style={{ padding: 10, borderRadius: 10, background: c.inp, border: `1px solid ${c.bd}`, textAlign: "center", fontFamily: "monospace", fontSize: 13, color: c.text, direction: "ltr" }}>{b}</div>)}
                </div>
                <div style={{ fontSize: 11, color: "#f59e0b", textAlign: "center", marginBottom: 18 }}>این کدها فقط همین یک‌بار نمایش داده می‌شوند. آن‌ها را یادداشت یا کپی کنید.</div>
                <button onClick={() => finish(pending)} style={primary(false)}>یادداشت کردم؛ ورود به سیستم</button>
              </>
            )}

            {step === "changepw" && (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.lab, display: "block", marginBottom: 7 }}>پسورد جدید</label>
                  <div style={field}><Lock size={15} color={c.sub} /><input type={showPass ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" style={{ ...bare, direction: "ltr" }} />
                    <button onClick={() => setShowPass(!showPass)} style={{ background: "none", border: "none", cursor: "pointer", color: c.sub, display: "flex", padding: 0 }}>{showPass ? <EyeOff size={15} /> : <Eye size={15} />}</button></div>
                  <div style={{ height: 5, borderRadius: 5, background: c.bd, marginTop: 8, overflow: "hidden" }}><div style={{ width: `${sc * 20}%`, height: "100%", background: sc < 3 ? "#ef4444" : sc < 5 ? "#f59e0b" : "#10b981", transition: "all .3s" }} /></div>
                  <div style={{ fontSize: 11, color: c.sub, marginTop: 6 }}>حداقل {minLen} کاراکتر؛ ترکیب حروف بزرگ و کوچک، عدد و علامت</div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.lab, display: "block", marginBottom: 7 }}>تکرار پسورد</label>
                  <div style={field}><Lock size={15} color={c.sub} /><input type={showPass ? "text" : "password"} value={newPw2} onChange={(e) => setNewPw2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitNewPw()} autoComplete="new-password" style={{ ...bare, direction: "ltr" }} /></div>
                </div>
                <button onClick={submitNewPw} disabled={isLoading || !newPw} style={primary(isLoading || !newPw)}>{isLoading ? spinner : "ثبت پسورد جدید"}</button>
                <button onClick={back} style={{ width: "100%", marginTop: 12, background: "none", border: "none", color: c.lab, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>انصراف</button>
              </>
            )}

            <p style={{ textAlign: "center", fontSize: 11, color: dark ? "#475569" : "#94a3b8", margin: "20px 0 0" }}>© 2025 {company.name} — تمام حقوق محفوظ است</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) { .login-left { display: none !important; } }
      `}</style>
    </div>
  );
}
